import React, { useEffect } from "react";
import { X } from "lucide-react";
import { useT } from "@/i18n";

const Legal = ({ open, kind, onClose }) => {
  const { lang } = useT();
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      window.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const title = kind === "terms"
    ? (lang === "fr" ? "CONDITIONS D'UTILISATION" : "TERMS OF SERVICE")
    : (lang === "fr" ? "POLITIQUE DE CONFIDENTIALITÉ" : "PRIVACY POLICY");

  return (
    <div
      data-testid="legal-modal"
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#faf7f2] max-w-3xl w-full max-h-[85vh] overflow-y-auto border border-black/10"
      >
        <div className="sticky top-0 z-10 bg-[#faf7f2]/95 backdrop-blur-xl border-b border-black/10 flex items-center justify-between px-8 py-5">
          <div className="overline">{title}</div>
          <button
            data-testid="legal-close"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center hover:bg-black/5 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-8 py-8 sm:px-12 sm:py-10 text-zinc-700 leading-relaxed space-y-5 text-sm">
          {kind === "terms"
            ? (lang === "fr" ? <TermsFR /> : <TermsEN />)
            : (lang === "fr" ? <PrivacyFR /> : <PrivacyEN />)}
        </div>
      </div>
    </div>
  );
};

const H2 = ({ children }) => <h2 className="font-display text-3xl tracking-tight text-[#14141a]">{children}</h2>;
const H3 = ({ children }) => <h3 className="font-display text-xl tracking-tight text-[#14141a] mt-6 mb-2">{children}</h3>;
const Stamp = ({ children }) => <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">{children}</p>;
const Mail = () => <a className="text-[#ea7a1f] copper-line" href="mailto:info@milysuperior.com">info@milysuperior.com</a>;

const TermsEN = () => (
  <>
    <H2>Terms of Service</H2>
    <Stamp>Last updated: January 2026</Stamp>
    <p>These Terms of Service govern your use of this website (the "Site") and the purchase of products and services from Mily Superior ("we", "us", "our"). By accessing or using the Site, you agree to these Terms.</p>
    <H3>1. Products & Availability</H3>
    <p>Product descriptions, technical data and images displayed on the Site are provided for general informational purposes. Availability of TOP-CAST® Decorative Surface Retarder and related items is subject to stock and may change without notice. TOP-CAST® is a trademark of GCP Applied Technologies, manufactured by Dayton Superior — Mily Superior is a distributor.</p>
    <H3>2. Quotes & Orders</H3>
    <p>Submitting a request via our quote form does not constitute a binding contract. All orders are subject to written confirmation, credit approval and acceptance by Mily Superior. Pricing, lead times and freight terms quoted are valid for the period stated in the quotation.</p>
    <H3>3. Use of the Site</H3>
    <p>You agree not to misuse the Site, attempt to gain unauthorized access, scrape content for commercial resale, or interfere with its normal operation. All content, marks and assets are the property of Mily Superior or their respective owners.</p>
    <H3>4. Disclaimers & Liability</H3>
    <p>Product information is provided "as is". Buyers and applicators are responsible for verifying suitability for their specific project and following the manufacturer's published Technical Data Sheet. To the maximum extent permitted by law, Mily Superior is not liable for indirect, incidental or consequential damages arising from use of the Site or its products.</p>
    <H3>5. Governing Law</H3>
    <p>These Terms are governed by the laws of the Province of Québec and the federal laws of Canada applicable therein. Any dispute will be resolved by the competent courts of the judicial district of Mascouche/Montréal.</p>
    <H3>6. Contact</H3>
    <p>Mily Superior · 1366 Avenue De La Gare, Mascouche, QC J7K 2Z2 · Canada<br />Phone: +1 514-594-1482 · Email: <Mail /></p>
  </>
);

const TermsFR = () => (
  <>
    <H2>Conditions d'utilisation</H2>
    <Stamp>Dernière mise à jour : janvier 2026</Stamp>
    <p>Les présentes Conditions d'utilisation régissent votre utilisation de ce site Web (le « Site ») et l'achat de produits et services auprès de Mily Superior (« nous », « notre »). En accédant au Site ou en l'utilisant, vous acceptez ces conditions.</p>
    <H3>1. Produits et disponibilité</H3>
    <p>Les descriptions de produits, données techniques et images affichées sur le Site sont fournies à titre informatif général. La disponibilité du retardateur de surface décoratif TOP-CAST® et des articles connexes est sujette aux stocks et peut changer sans préavis. TOP-CAST® est une marque de commerce de GCP Applied Technologies, fabriquée par Dayton Superior — Mily Superior est un distributeur.</p>
    <H3>2. Soumissions et commandes</H3>
    <p>L'envoi d'une demande via notre formulaire de soumission ne constitue pas un contrat exécutoire. Toutes les commandes sont assujetties à une confirmation écrite, à l'approbation du crédit et à l'acceptation par Mily Superior. Les prix, délais et conditions de livraison cités sont valables pour la période indiquée dans la soumission.</p>
    <H3>3. Utilisation du Site</H3>
    <p>Vous acceptez de ne pas faire mauvais usage du Site, de ne pas tenter d'y accéder sans autorisation, d'en extraire le contenu à des fins commerciales ou d'interférer avec son fonctionnement. Tout le contenu, les marques et les éléments sont la propriété de Mily Superior ou de leurs détenteurs respectifs.</p>
    <H3>4. Avis et responsabilité</H3>
    <p>Les informations sur les produits sont fournies « telles quelles ». Les acheteurs et applicateurs sont responsables de vérifier la pertinence pour leur projet et de suivre la fiche technique publiée par le fabricant. Dans la mesure maximale permise par la loi, Mily Superior n'est pas responsable des dommages indirects, accessoires ou consécutifs découlant de l'utilisation du Site ou de ses produits.</p>
    <H3>5. Droit applicable</H3>
    <p>Les présentes Conditions sont régies par les lois de la Province de Québec et les lois fédérales du Canada qui y sont applicables. Tout litige sera porté devant les tribunaux compétents du district judiciaire de Mascouche/Montréal.</p>
    <H3>6. Contact</H3>
    <p>Mily Superior · 1366 Avenue De La Gare, Mascouche, QC J7K 2Z2 · Canada<br />Téléphone : +1 514-594-1482 · Courriel : <Mail /></p>
  </>
);

const PrivacyEN = () => (
  <>
    <H2>Privacy Policy</H2>
    <Stamp>Last updated: January 2026</Stamp>
    <p>This Privacy Policy explains how Mily Superior ("we", "us", "our") collects, uses and protects information you provide on this Site, in compliance with Québec's Law 25 and applicable Canadian privacy legislation.</p>
    <H3>1. Information We Collect</H3>
    <p>When you submit our quote form we collect your name, email, phone number, company (optional) and project details. We may also collect basic technical data such as IP address, browser type and pages viewed for analytics and security.</p>
    <H3>2. How We Use Information</H3>
    <p>Your information is used to respond to your inquiry, provide product quotes and technical support, manage our customer relationship, and improve the Site. We do not sell your personal information.</p>
    <H3>3. Sharing</H3>
    <p>We may share information with trusted service providers (hosting, email delivery, analytics) strictly to operate the Site and respond to you. These providers are bound to protect your data and use it only on our instructions.</p>
    <H3>4. Retention</H3>
    <p>We retain quote inquiries for as long as is reasonably necessary to provide our services and meet legal obligations, after which they are securely deleted or anonymized.</p>
    <H3>5. Your Rights</H3>
    <p>Under Québec Law 25 and Canadian privacy law you have the right to access, correct, or request deletion of your personal information, and to withdraw consent at any time. Send any request to <Mail />.</p>
    <H3>6. Cookies</H3>
    <p>We use a minimal set of cookies and local storage for basic site functionality and anonymized analytics. You can disable cookies in your browser settings.</p>
    <H3>7. Contact</H3>
    <p>Privacy Officer · Mily Superior<br />1366 Avenue De La Gare, Mascouche, QC J7K 2Z2 · Canada<br />Phone: +1 514-594-1482 · Email: <Mail /></p>
  </>
);

const PrivacyFR = () => (
  <>
    <H2>Politique de confidentialité</H2>
    <Stamp>Dernière mise à jour : janvier 2026</Stamp>
    <p>La présente Politique de confidentialité explique comment Mily Superior (« nous », « notre ») collecte, utilise et protège les informations que vous fournissez sur ce Site, conformément à la Loi 25 du Québec et à la législation canadienne applicable sur la protection des renseignements personnels.</p>
    <H3>1. Informations recueillies</H3>
    <p>Lorsque vous soumettez notre formulaire de soumission, nous recueillons votre nom, courriel, numéro de téléphone, entreprise (facultatif) et les détails de votre projet. Nous pouvons également recueillir des données techniques de base telles que l'adresse IP, le type de navigateur et les pages consultées à des fins d'analyse et de sécurité.</p>
    <H3>2. Utilisation des informations</H3>
    <p>Vos informations sont utilisées pour répondre à votre demande, fournir des soumissions et un soutien technique, gérer notre relation avec la clientèle et améliorer le Site. Nous ne vendons pas vos renseignements personnels.</p>
    <H3>3. Partage</H3>
    <p>Nous pouvons partager des informations avec des fournisseurs de services de confiance (hébergement, envoi de courriels, analyses) strictement pour exploiter le Site et vous répondre. Ces fournisseurs sont tenus de protéger vos données et de ne les utiliser que selon nos instructions.</p>
    <H3>4. Conservation</H3>
    <p>Nous conservons les demandes de soumission aussi longtemps que raisonnablement nécessaire pour fournir nos services et satisfaire à nos obligations légales, après quoi elles sont supprimées ou anonymisées de manière sécurisée.</p>
    <H3>5. Vos droits</H3>
    <p>En vertu de la Loi 25 du Québec et de la législation canadienne sur la vie privée, vous avez le droit d'accéder à vos renseignements personnels, de les rectifier ou d'en demander la suppression, et de retirer votre consentement à tout moment. Toute demande peut être envoyée à <Mail />.</p>
    <H3>6. Témoins (cookies)</H3>
    <p>Nous utilisons un ensemble minimal de témoins et de stockage local pour les fonctionnalités de base du Site et des analyses anonymisées. Vous pouvez désactiver les témoins dans les paramètres de votre navigateur.</p>
    <H3>7. Contact</H3>
    <p>Responsable de la protection des renseignements personnels · Mily Superior<br />1366 Avenue De La Gare, Mascouche, QC J7K 2Z2 · Canada<br />Téléphone : +1 514-594-1482 · Courriel : <Mail /></p>
  </>
);

export default Legal;
